import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Plus, Download, Upload, FileSpreadsheet,
    X, Search, Users, FileDown, CheckCircle, XCircle, AlertCircle, RefreshCw, Eye, Pencil, Save, ShieldCheck
} from "lucide-react";
import ScrollToTop from "../../Ui/UiJs/ScrollToTop";
import "./UserMassImport.css";

import {
    getAllUsersRequest,
    downloadUsersTemplateRequest,
    reviewUsersMassImportRequest,
    importUsersMassRequest,
    clearMassImportState,
    exportUsersRequest,
    updateUserRequest,
    updateUserAccountStatusRequest,
    uploadAttestationRibRequest,
    createUserRequest,
} from "../../redux/actions/userActions";
import { getAllJobsRequest } from "../../redux/actions/Jobactions";
import { getAllEntitesRequest } from "../../redux/actions/entitesActions";

const Modal = ({ open, title, onClose, footer, children }) => {
    if (!open) return null;
    return (
        <div className="um-overlay" onMouseDown={onClose}>
            <div className="um-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="um-modal-header">
                    <h3>{title}</h3>
                    <button className="um-modal-close" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="um-modal-body">{children}</div>
                {footer && <div className="um-modal-footer">{footer}</div>}
            </div>
        </div>
    );
};


const emptyUserForm = {
    employeeCode: "",
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    dateOfBirth: "",
    genre: "",
    hireDate: "",
    jobId: "",
    jobEffectiveDate: "",
    entityId: "",
    managerId: "",
    accountStatus: "ACTIVE",
    profilePictureUrl: "",
    bannerPictureUrl: "",
    city: "",
    bio: "",
    cin: "",
    cnss: "",
    passeport: "",
    carteSejour: "",
    phoneUrgence: "",
    adresse: "",
    codePostal: "",
    statutMarital: "",
    nombreEnfants: "",
    personneUrgence: "",
    rib: "",
    attestationRib: "",
    attestationRibFile: null,
    iban: "",
    banque: "",
    typeContrat: "",
    category: "",
    password: "",
};

const userFullName = (user) =>
    user?.fullName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Utilisateur";

const ACCOUNT_STATUS_OPTIONS = [
    { value: "ACTIVE", label: "Actif" },
    { value: "INACTIVE", label: "Inactif" },
    { value: "SUSPENDED", label: "Suspendu" },
    { value: "PENDING", label: "En attente" },
];

const getAccountStatusLabel = (status) =>
    ACCOUNT_STATUS_OPTIONS.find((item) => item.value === status)?.label || status || "Inconnu";

const isUserActive = (user) => user?.accountStatus === "ACTIVE" && user?.isActive === true;
const isFirstAccountActivation = (user) => !user?.credentialsSentAt;

const toInputDate = (value) => value ? String(value).slice(0, 10) : "";

const normalizeImportKey = (key) =>
    String(key || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, "");

const getImportField = (row, ...aliases) => {
    const normalizedRow = Object.entries(row || {}).reduce((acc, [key, value]) => {
        acc[normalizeImportKey(key)] = value;
        return acc;
    }, {});

    for (const alias of aliases) {
        const value = normalizedRow[normalizeImportKey(alias)];
        if (value !== undefined && value !== null && String(value).trim() !== "") {
            return value;
        }
    }

    return "";
};

const formatImportDate = (value) => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().slice(0, 10);
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toISOString().slice(0, 10);
    }

    const trimmed = String(value).trim();
    return trimmed || null;
};

const makeUserForm = (user = {}) => ({
    ...emptyUserForm,
    employeeCode: user.employeeCode || "",
    username: user.username || "",
    email: user.email || "",
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    phone: user.phone || "",
    dateOfBirth: toInputDate(user.dateOfBirth),
    genre: user.genre || "",
    hireDate: toInputDate(user.hireDate),
    jobId: user.jobId != null ? String(user.jobId) : (user.job?.id != null ? String(user.job.id) : ""),
    jobEffectiveDate: "",
    entityId: user.entityId != null ? String(user.entityId) : (user.entity?.id != null ? String(user.entity.id) : ""),
    managerId: user.managerId != null ? String(user.managerId) : (user.manager?.id != null ? String(user.manager.id) : ""),
    accountStatus: user.accountStatus || "ACTIVE",
    profilePictureUrl: user.profilePictureUrl || "",
    bannerPictureUrl: user.bannerPictureUrl || "",
    city: user.city || "",
    bio: user.bio || "",
    cin: user.cin || "",
    cnss: user.cnss || "",
    passeport: user.passeport || "",
    carteSejour: user.carteSejour || "",
    phoneUrgence: user.phoneUrgence || "",
    adresse: user.adresse || "",
    codePostal: user.codePostal || "",
    statutMarital: user.statutMarital || "",
    nombreEnfants: user.nombreEnfants != null ? String(user.nombreEnfants) : "",
    personneUrgence: user.personneUrgence || "",
    rib: user.rib || "",
    attestationRib: user.attestationRib || "",
    attestationRibFile: null,
    iban: user.iban || "",
    banque: user.banque || "",
    typeContrat: user.typeContrat || "",
    category: user.category || "",
});

const DetailItem = ({ label, value }) => (
    <div className="um-detail-item">
        <span>{label}</span>
        <strong>{value || "—"}</strong>
    </div>
);

export default function UserMassImport() {
    const dispatch = useDispatch();
    const { users, loading, massImportReview, massImportResult } = useSelector((s) => s.user);
    const jobs = useSelector((s) => s.jobs?.jobs || []);
    const entites = useSelector((s) => s.entite?.entites || []);
    const currentUser = useSelector((state) => state?.auth?.user);
    const currentCompanyId = currentUser?.companyId;

    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkFile, setBulkFile] = useState(null);
    const [fileName, setFileName] = useState("");
    const [bulkAction, setBulkAction] = useState("add");
    const [rows, setRows] = useState([]);
    const [existingQuery, setExistingQuery] = useState("");
    const [filter, setFilter] = useState("all");
    const [detailUser, setDetailUser] = useState(null);
    const [editUser, setEditUser] = useState(null);
    const [editForm, setEditForm] = useState(emptyUserForm);
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState(emptyUserForm);
    const [accountUser, setAccountUser] = useState(null);
    const [accountForm, setAccountForm] = useState({
        accountStatus: "ACTIVE",
        startDate: "",
        endDate: "",
        sendEmail: true,
        reason: "",
    });
    const [ribModalOpen, setRibModalOpen] = useState(false);
    const [ribFileName, setRibFileName] = useState("");
    const [ribRows, setRibRows] = useState([]);

    const openRibModal = () => {
        setRibFileName("");
        setRibRows([]);
        setRibModalOpen(true);
    };

    const closeRibModal = () => {
        setRibModalOpen(false);
        setRibFileName("");
        setRibRows([]);
    };

    const handleRibFile = async (file) => {
        if (!file) return;
        setRibFileName(file.name);
        const data = await file.arrayBuffer();
        const wb = XLSX.read(data, { type: "array", cellDates: true });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        const mapped = json.map((r) => ({
            employeeCode: String(getImportField(r, "employeeCode", "EmployeeCode", "Matricule")).trim() || null,
            rib: String(getImportField(r, "rib", "Rib", "RIB")).trim() || null,
            banque: String(getImportField(r, "banque", "Banque", "Bank")).trim() || null,
            iban: String(getImportField(r, "iban", "Iban", "IBAN")).trim() || null,
        })).filter((r) => r.employeeCode);
        setRibRows(mapped);
    };

    const downloadRibTemplate = () => {
        const templateData = [
            { Matricule: "EMP001", RIB: "123456789012345678901234", Banque: "Attijariwafa Bank", IBAN: "MA64123456789012345678901234" },
            { Matricule: "EMP002", RIB: "987654321098765432109876", Banque: "CIH Bank", IBAN: "MA64987654321098765432109876" }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template_RIB_Banque");
        XLSX.writeFile(wb, "Template_Import_RIB_Banque.xlsx");
    };

    const onImportRib = () => {
        if (!currentCompanyId || !ribRows.length) return;
        Swal.fire({
            title: "Confirmer l'affectation",
            text: `Mettre à jour les RIBs et Banques de ${ribRows.length} utilisateur(s) ?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#2a5ca9",
            confirmButtonText: "Oui, mettre à jour"
        }).then((res) => {
            if (res.isConfirmed) {
                dispatch(importUsersMassRequest(currentCompanyId, "edit", ribRows));
                closeRibModal();
            }
        });
    };


    const refreshData = () => dispatch(getAllUsersRequest());
    useEffect(() => {
        refreshData();
        dispatch(getAllJobsRequest());
        dispatch(getAllEntitesRequest());
    }, [dispatch]);

    const resetBulkState = () => {
        setBulkFile(null);
        setFileName("");
        setRows([]);
        setBulkAction("add");
        dispatch(clearMassImportState());
    };

    const openBulkModal = () => {
        resetBulkState();
        setBulkOpen(true);
    };

    const closeBulkModal = () => {
        setBulkOpen(false);
        resetBulkState();
    };

    useEffect(() => {
        if (!massImportResult) return;
        const importedUsers = Array.isArray(massImportResult) ? massImportResult : [];
        const limitedUsers = importedUsers.filter((user) =>
            String(user?.accountStatusReason || "").toLowerCase().includes("limite de comptes actifs")
        );

        closeBulkModal();
        Swal.fire({
            icon: "success",
            title: "Import terminé",
            html: limitedUsers.length
                ? [
                    `<p>${importedUsers.length} utilisateur(s) créé(s).</p>`,
                    `<p><strong>${limitedUsers.length}</strong> créé(s), mais non activé(s) : limite de comptes actifs atteinte.</p>`,
                    `<ul style="text-align:left; margin: 0; padding-left: 18px;">${limitedUsers
                        .map((user) => `<li>${userFullName(user)}${user?.email ? ` (${user.email})` : ""}</li>`)
                        .join("")}</ul>`,
                  ].join("")
                : `<p>${importedUsers.length} utilisateur(s) créé(s) avec succès.</p>`,
        });
    }, [massImportResult]);

    const openDetail = (user) => setDetailUser(user);

    const openEdit = (user) => {
        setEditUser(user);
        setEditForm(makeUserForm(user));
    };

    const closeEdit = () => {
        setEditUser(null);
        setEditForm(emptyUserForm);
    };

    const setEditValue = (key, value) => {
        setEditForm((prev) => ({ ...prev, [key]: value }));
    };

    const openCreate = () => {
        setCreateForm({ ...emptyUserForm });
        setCreateOpen(true);
    };

    const closeCreate = () => {
        setCreateOpen(false);
        setCreateForm(emptyUserForm);
    };

    const openAccountModal = (user) => {
        setAccountUser(user);
        setAccountForm({
            accountStatus: user?.accountStatus || "ACTIVE",
            startDate: user?.accountActivationStartDate ? String(user.accountActivationStartDate).slice(0, 16) : "",
            endDate: user?.accountActivationEndDate ? String(user.accountActivationEndDate).slice(0, 16) : "",
            sendEmail: true,
            reason: user?.accountStatusReason || "",
        });
    };

    const closeAccountModal = () => {
        setAccountUser(null);
        setAccountForm({
            accountStatus: "ACTIVE",
            startDate: "",
            endDate: "",
            sendEmail: true,
            reason: "",
        });
    };

    useEffect(() => {
        if (accountForm.accountStatus === "ACTIVE" && !accountForm.sendEmail) {
            setAccountForm((prev) => ({ ...prev, sendEmail: true }));
        }
    }, [accountForm.accountStatus, accountForm.sendEmail]);

    const setCreateValue = (key, value) => {
        setCreateForm((prev) => ({ ...prev, [key]: value }));
    };

    const setAccountValue = (key, value) => {
        setAccountForm((prev) => ({ ...prev, [key]: value }));
    };

    const userToPayload = (form) => ({
    employeeCode: form.employeeCode || null,
    username: form.username || form.email || form.phone || null,
        email: form.email || null,
        firstName: form.firstName || null,
        lastName: form.lastName || null,
        phone: form.phone || null,
        dateOfBirth: form.dateOfBirth || null,
        genre: form.genre || null,
        hireDate: form.hireDate || null,
        jobId: form.jobId ? Number(form.jobId) : null,
        jobEffectiveDate: form.jobEffectiveDate || null,
        entityId: form.entityId ? Number(form.entityId) : null,
        managerId: form.managerId ? Number(form.managerId) : null,
        accountStatus: form.accountStatus || "ACTIVE",
        profilePictureUrl: form.profilePictureUrl || null,
        bannerPictureUrl: form.bannerPictureUrl || null,
        city: form.city || null,
        bio: form.bio || null,
        cin: form.cin || null,
        cnss: form.cnss || null,
        passeport: form.passeport || null,
        carteSejour: form.carteSejour || null,
        phoneUrgence: form.phoneUrgence || null,
        adresse: form.adresse || null,
        codePostal: form.codePostal || null,
        statutMarital: form.statutMarital || null,
        nombreEnfants: form.nombreEnfants !== "" ? Number(form.nombreEnfants) : null,
        personneUrgence: form.personneUrgence || null,
        rib: form.rib || null,
        attestationRib: form.attestationRib || null,
        iban: form.iban || null,
        banque: form.banque || null,
        typeContrat: form.typeContrat || null,
        category: form.category || null,
    });

    const onSaveUser = () => {
        if (!editUser?.id) return;
        Swal.fire({
            title: "Confirmer la modification",
            text: "Êtes-vous sur de vouloir modifier les informations de cet utilisateur ?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#2a5ca9",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Oui, modifier",
            cancelButtonText: "Annuler",
        }).then((res) => {
            if (res.isConfirmed) {
                dispatch(updateUserRequest(editUser.id, userToPayload(editForm)));
                if (editForm.attestationRibFile) {
                    dispatch(uploadAttestationRibRequest(editUser.id, editForm.attestationRibFile));
                }
                closeEdit();
                Swal.fire({
                    icon: "success",
                    title: "Utilisateur mis à jour",
                    timer: 1800,
                    showConfirmButton: false,
                });
            }
        });
    };

    const onCreateUser = () => {
        if (!createForm.firstName || !createForm.lastName) {
            Swal.fire({ icon: "warning", title: "Champs requis", text: "Prénom et nom sont obligatoires." });
            return;
        }
        if (!createForm.email && !createForm.phone) {
            Swal.fire({ icon: "warning", title: "Canal requis", text: "Un e-mail ou un numéro de téléphone est obligatoire." });
            return;
        }
        if (createForm.email && !createForm.password) {
            Swal.fire({ icon: "warning", title: "Mot de passe requis", text: "Le mot de passe est obligatoire quand un e-mail est renseigné." });
            return;
        }
        Swal.fire({
            title: "Confirmer la création",
            text: "Êtes-vous sûr de vouloir créer cet utilisateur ?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#2a5ca9",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Oui, créer",
            cancelButtonText: "Annuler",
        }).then((res) => {
            if (res.isConfirmed) {
                const { username, ...basePayload } = userToPayload(createForm);
                const payload = {
                    ...basePayload,
                    password: createForm.password,
                };
                dispatch(createUserRequest(payload));
                closeCreate();
            }
        });
    };

    const onUpdateAccountStatus = () => {
        if (!accountUser?.id) return;
        if (accountForm.accountStatus === "ACTIVE" && !accountForm.sendEmail) {
            Swal.fire({
                icon: "warning",
                title: "E-mail obligatoire",
                text: "Obligatoire pour transmettre les nouveaux identifiants temporaires à l’utilisateur."
            });
            return;
        }
        dispatch(updateUserAccountStatusRequest(accountUser.id, {
            accountStatus: accountForm.accountStatus,
            startDate: accountForm.startDate || null,
            endDate: accountForm.endDate || null,
            sendEmail: !!accountForm.sendEmail,
            reason: accountForm.reason || null,
        }));
        closeAccountModal();
    };

    const handleFile = async (file) => {
        if (!file) return;
        dispatch(clearMassImportState());
        setFileName(file.name);
        setBulkFile(file);
        const data = await file.arrayBuffer();
        const wb = XLSX.read(data, { type: "array", cellDates: true });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        const mapped = json.map((r) => ({
            employeeCode: String(getImportField(r, "employeeCode", "EmployeeCode", "Matricule")).trim() || null,
            email: String(getImportField(r, "email", "Email")).trim() || null,
            firstName: String(getImportField(r, "firstName", "FirstName", "Prenom", "Prénom")).trim() || null,
            lastName: String(getImportField(r, "lastName", "LastName", "Nom")).trim() || null,
            phone: String(getImportField(r, "phone", "Phone", "Téléphone", "Telephone")).trim() || null,
            dateOfBirth: formatImportDate(getImportField(r, "dateOfBirth", "DateOfBirth")),
            genre: String(getImportField(r, "genre", "Genre", "Sexe")).trim().toUpperCase() || null,
            hireDate: formatImportDate(getImportField(r, "hireDate", "HireDate", "DateEntree", "DateEntrée")),
            jobCode: String(getImportField(r, "jobCode", "JobCode")).trim() || null,
            jobEffectiveDate: formatImportDate(getImportField(r, "jobEffectiveDate", "JobEffectiveDate", "dateEffet", "DateEffet", "Date d'effet")),
            entityCode: String(getImportField(r, "entityCode", "EntityCode")).trim() || null,
            managerEmployeeCode: String(getImportField(r, "managerEmployeeCode", "ManagerEmployeeCode")).trim() || null,
            status: String(getImportField(r, "status", "Status")).trim() || null,
            accountStatus: String(getImportField(r, "accountStatus", "AccountStatus", "EtatCompte", "EtatDuCompte")).trim() || null,
            category: String(getImportField(r, "category", "Category")).trim() || null,
            typeContrat: String(getImportField(r, "typeContrat", "TypeContrat")).trim() || null
        })).filter((r) => r.employeeCode);
        setRows(mapped);
    };

    const filteredExisting = useMemo(() => {
        const q = existingQuery.trim().toLowerCase();
        return (users || []).filter((u) => {
            const matchesQ = !q || (u.employeeCode || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || userFullName(u).toLowerCase().includes(q);
            const matchesF = filter === "all" ? true : (filter === "active" ? isUserActive(u) : !isUserActive(u));
            return matchesQ && matchesF;
        });
    }, [users, existingQuery, filter]);

    const accountCapacity = useMemo(() => {
        const accountInfo = (users || []).find((u) =>
            u.activeUsersCount != null || u.maxActiveUsers != null
        );

        return accountInfo
            ? { active: accountInfo.activeUsersCount, max: accountInfo.maxActiveUsers }
            : null;
    }, [users]);

    const onImport = () => {
        if (!currentCompanyId || !rows.length || hasErrors) return;
        Swal.fire({
            title: "Confirmer l'import", text: `Importer ${rows.length} utilisateur(s) ?`, icon: "question",
            showCancelButton: true, confirmButtonColor: "#2a5ca9", confirmButtonText: "Oui, importer"
        }).then((res) => {
            if (res.isConfirmed) {
                dispatch(importUsersMassRequest(currentCompanyId, bulkAction, rows));
                setBulkOpen(false);
            }
        });
    };

    const hasErrors = (massImportReview?.errors || []).length > 0;

    return (
        <div className="um-page">
            <div className="um-header">
                <div>
                    <h1>Liste des utilisateurs</h1>
                    <p>Gérez vos collaborateurs et leurs accès en masse</p>
                </div>
                <div className="um-header-actions">
                    <button className="um-btn um-btn-outline" onClick={() => dispatch(downloadUsersTemplateRequest())}>
                        <Download size={14} /> Template
                    </button>
                    <button className="um-btn um-btn-outline" onClick={() => dispatch(exportUsersRequest())}>
                        <FileDown size={14} /> Exporter
                    </button>
                    <button className="um-btn um-btn-outline" onClick={openRibModal}>
                        <FileSpreadsheet size={14} /> Import RIB / Banque
                    </button>
                    <button className="um-btn um-btn-primary" onClick={openBulkModal}>
                        <Upload size={14} /> Import massif
                    </button>
                    <button className="um-btn um-btn-success" onClick={openCreate}>
                        <Plus size={14} /> Ajouter un nouvel utilisateur
                    </button>
                </div>
            </div>

            <div className="um-toolbar">
                <div className="um-search-wrapper">
                    <Search className="um-search-icon" size={14} />
                    <input className="um-search" placeholder="Rechercher par matricule, nom..." value={existingQuery} onChange={(e) => setExistingQuery(e.target.value)} />
                </div>
                <div className="um-filter-tabs">
                    {[{ k: "all", l: "Tous" }, { k: "active", l: "Actifs" }, { k: "inactive", l: "Inactifs" }].map((f) => (
                        <button key={f.k} className={`um-filter-tab ${filter === f.k ? 'active' : ''}`} onClick={() => setFilter(f.k)}>{f.l}</button>
                    ))}
                </div>
                <div className="um-count">{filteredExisting.length} utilisateur{filteredExisting.length > 1 ? "s" : ""}</div>
                {accountCapacity && (
                    <div className="um-account-capacity" title="Comptes actifs">
                        <span>Comptes actifs</span>
                        <strong>{accountCapacity.active ?? 0}{accountCapacity.max != null ? ` / ${accountCapacity.max}` : ""}</strong>
                    </div>
                )}
            </div>

            <div className="um-table-wrapper">
                <table className="um-table">
                    <thead>
                        <tr>
                            <th>Matricule</th>
                            <th>Nom complet</th>
                            <th>Poste</th>
                            <th>Entité</th>
                            <th>Manager</th>
                            <th>Contrat</th>
                            <th>Statut</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8}><div className="um-loading"><div className="um-spinner" /><span>Chargement...</span></div></td></tr>
                        ) : filteredExisting.length === 0 ? (
                            <tr><td colSpan={8} className="um-empty">Aucun utilisateur trouvé</td></tr>
                        ) : filteredExisting.slice(0, 50).map((u) => (
                            <tr key={u.id}>
                                <td><span className="um-strong">{u.employeeCode || "—"}</span></td>
                                <td className="um-name">{userFullName(u)}</td>
                                <td className="um-muted">{u.jobName || "—"}</td>
                                <td className="um-muted">{u.entityName || "—"}</td>
                                <td className="um-muted">{u.managerFullName || u.managerName || "—"}</td>
                                <td><span className="um-badge um-badge-blue">{u.typeContrat || "—"}</span></td>
                                <td><span className={`um-badge ${isUserActive(u) ? 'um-badge-green' : 'um-badge-grey'}`}>{getAccountStatusLabel(u.accountStatus)}</span></td>
                                <td>
                                    <div className="um-row-actions">
                                        <button type="button" className="um-icon-btn" onClick={() => openDetail(u)} title="Afficher les details" aria-label="Afficher les details">
                                            <Eye size={15} />
                                        </button>
                                        <button type="button" className="um-icon-btn" onClick={() => openAccountModal(u)} title="Gestion du compte" aria-label="Gestion du compte">
                                            <ShieldCheck size={15} />
                                        </button>
                                        <button type="button" className="um-icon-btn um-icon-btn-primary" onClick={() => openEdit(u)} title="Modifier" aria-label="Modifier">
                                            <Pencil size={15} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal open={!!detailUser} title={`Details - ${userFullName(detailUser)}`} onClose={() => setDetailUser(null)}>
                {detailUser && (
                    <div className="um-detail-grid">
                        <DetailItem label="Matricule" value={detailUser.employeeCode} />
                        <DetailItem label="Username" value={detailUser.username} />
                        <DetailItem label="Email" value={detailUser.email} />
                        <DetailItem label="Téléphone" value={detailUser.phone} />
                        <DetailItem label="Poste" value={detailUser.jobName} />
                        <DetailItem label="Entité" value={detailUser.entityName} />
                        <DetailItem label="Manager" value={detailUser.managerFullName || detailUser.managerName} />
                        <DetailItem label="État du compte" value={getAccountStatusLabel(detailUser.accountStatus)} />
                        <DetailItem label="Date naissance" value={detailUser.dateOfBirth} />
                        <DetailItem label="Date embauche" value={detailUser.hireDate} />
                        <DetailItem label="CIN" value={detailUser.cin} />
                        <DetailItem label="CNSS" value={detailUser.cnss} />
                        <DetailItem label="Passeport" value={detailUser.passeport} />
                        <DetailItem label="Carte sejour" value={detailUser.carteSejour} />
                        <DetailItem label="Ville" value={detailUser.city} />
                        <DetailItem label="Adresse" value={detailUser.adresse} />
                        <DetailItem label="Code postal" value={detailUser.codePostal} />
                        <DetailItem label="Statut marital" value={detailUser.statutMarital} />
                        <DetailItem label="Nombre d'enfants" value={detailUser.nombreEnfants} />
                        <DetailItem label="Personne d'urgence" value={detailUser.personneUrgence} />
                        <DetailItem label="Téléphone d'urgence" value={detailUser.phoneUrgence} />
                        <DetailItem label="RIB" value={detailUser.rib} />
                        <DetailItem label="Attestation RIB" value={detailUser.attestationRib} />
                        <DetailItem label="IBAN" value={detailUser.iban} />
                        <DetailItem label="Banque" value={detailUser.banque} />
                        <DetailItem label="Contrat" value={detailUser.typeContrat} />
                        <DetailItem label="Catégorie" value={detailUser.category} />
                    </div>
                )}
            </Modal>

            <Modal open={!!accountUser} title="Gestion du compte utilisateur" onClose={closeAccountModal} footer={
                <>
                    <button className="um-btn um-btn-outline" onClick={closeAccountModal}>Annuler</button>
                    <button className="um-btn um-btn-primary" onClick={onUpdateAccountStatus}>
                        <ShieldCheck size={14} /> Confirmer
                    </button>
                </>
            }>
                {accountUser && (
                    <div className="um-form-section">
                        <div className="um-detail-grid" style={{ marginBottom: 18 }}>
                            <DetailItem label="Utilisateur" value={userFullName(accountUser)} />
                            <DetailItem label="Email" value={accountUser.email} />
                            <DetailItem label="Entreprise" value={accountUser.companyName} />
                            <DetailItem label="État actuel du compte" value={getAccountStatusLabel(accountUser.accountStatus)} />
                            <DetailItem label="Comptes actifs" value={accountUser.activeUsersCount != null && accountUser.maxActiveUsers != null ? `${accountUser.activeUsersCount} / ${accountUser.maxActiveUsers}` : "Non défini"} />
                            <DetailItem label="Éligibilité suppression" value={accountUser.deletionCandidate ? `Oui${accountUser.deletionEligibleAt ? ` - ${String(accountUser.deletionEligibleAt).slice(0, 10)}` : ""}` : "Non"} />
                        </div>

                        <div className="um-alert um-alert-success" style={{ marginBottom: 16 }}>
                            <AlertCircle size={16} />
                            Activer : l'utilisateur pourra se connecter. Suspendre : l'accès sera temporairement bloqué. Désactiver : l'accès sera bloqué jusqu'à une nouvelle activation.
                        </div>

                        <div className="um-form-grid">
                            <div className="um-field">
                                <label>État du compte</label>
                                <select value={accountForm.accountStatus} onChange={(e) => setAccountValue("accountStatus", e.target.value)}>
                                    {ACCOUNT_STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="um-field">
                                <label>Date de début</label>
                                <input type="datetime-local" value={accountForm.startDate} onChange={(e) => setAccountValue("startDate", e.target.value)} />
                            </div>
                            <div className="um-field">
                                <label>Date de fin</label>
                                <input type="datetime-local" value={accountForm.endDate} onChange={(e) => setAccountValue("endDate", e.target.value)} />
                            </div>
                            <div className="um-field um-field-full">
                                <label>Motif / commentaire</label>
                                <textarea rows={3} value={accountForm.reason} onChange={(e) => setAccountValue("reason", e.target.value)} />
                            </div>
                            <div className="um-field um-field-full">
                                <label className="cm-check-label">
                                    <input
                                        type="checkbox"
                                        checked={accountForm.sendEmail}
                                        disabled={accountForm.accountStatus === "ACTIVE"}
                                        onChange={(e) => setAccountValue("sendEmail", e.target.checked)}
                                    />
                                    Envoyer un e-mail à l'utilisateur
                                </label>
                                {accountForm.accountStatus === "ACTIVE" && (
                                    <small style={{ color: "#b45309" }}>
                                        Obligatoire pour transmettre les nouveaux identifiants temporaires à l’utilisateur.
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            <Modal open={!!editUser} title={`Modifier - ${userFullName(editUser)}`} onClose={closeEdit} footer={
                <>
                    <button className="um-btn um-btn-outline" onClick={closeEdit}>Annuler</button>
                    <button className="um-btn um-btn-primary" onClick={onSaveUser}>
                        <Save size={14} /> Enregistrer
                    </button>
                </>
            }>
                <div className="um-form-section">
                    <h3 className="um-form-title">Identité</h3>
                    <div className="um-form-grid">
                        <div className="um-field">
                            <label>Matricule</label>
                            <input value={editForm.employeeCode} onChange={(e) => setEditValue("employeeCode", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Username</label>
                            <input value={editForm.username} onChange={(e) => setEditValue("username", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Prénom</label>
                            <input value={editForm.firstName} onChange={(e) => setEditValue("firstName", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Nom</label>
                            <input value={editForm.lastName} onChange={(e) => setEditValue("lastName", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Email</label>
                            <input type="email" value={editForm.email} onChange={(e) => setEditValue("email", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Téléphone</label>
                            <input value={editForm.phone} onChange={(e) => setEditValue("phone", e.target.value)} />
                        </div>
                    </div>
                </div>

                <div className="um-form-section">
                    <h3 className="um-form-title">Informations RH</h3>
                    <div className="um-form-grid">
                        <div className="um-field">
                            <label>Date d'embauche</label>
                            <input type="date" value={editForm.hireDate} onChange={(e) => setEditValue("hireDate", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Date de naissance</label>
                            <input type="date" value={editForm.dateOfBirth} onChange={(e) => setEditValue("dateOfBirth", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Genre</label>
                            <select value={editForm.genre} onChange={(e) => setEditValue("genre", e.target.value)}>
                                <option value="">-- Non renseigné --</option>
                                <option value="F">Femme</option>
                                <option value="H">Homme</option>
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Poste</label>
                            <select value={editForm.jobId} onChange={(e) => setEditValue("jobId", e.target.value)}>
                                <option value="">-- Aucun poste --</option>
                                {(jobs || []).map((job) => (
                                    <option key={job.id} value={String(job.id)}>
                                        {[job.code, job.name].filter(Boolean).join(" - ") || `Poste #${job.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Date d'effet du poste</label>
                            <input
                                type="date"
                                value={editForm.jobEffectiveDate}
                                onChange={(e) => setEditValue("jobEffectiveDate", e.target.value)}
                            />
                        </div>
                        <div className="um-field">
                            <label>Entité</label>
                            <select value={editForm.entityId} onChange={(e) => setEditValue("entityId", e.target.value)}>
                                <option value="">-- Aucune entité --</option>
                                {(entites || []).map((entite) => (
                                    <option key={entite.id} value={String(entite.id)}>
                                        {[entite.code, entite.name].filter(Boolean).join(" - ") || `Entité #${entite.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Manager</label>
                            <select value={editForm.managerId} onChange={(e) => setEditValue("managerId", e.target.value)}>
                                <option value="">-- Aucun manager --</option>
                                {(users || []).filter(isUserActive).filter((u) => u.id !== editUser?.id).map((u) => (
                                    <option key={u.id} value={String(u.id)}>
                                        {u.employeeCode ? `[${u.employeeCode}] ` : ""}{userFullName(u)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="um-field">
                            <label>État du compte</label>
                            <select value={editForm.accountStatus} onChange={(e) => setEditValue("accountStatus", e.target.value)}>
                                {ACCOUNT_STATUS_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Type contrat</label>
                            <select value={editForm.typeContrat} onChange={(e) => setEditValue("typeContrat", e.target.value)}>
                                <option value="">--</option>
                                <option value="CDI">CDI</option>
                                <option value="CDD">CDD</option>
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Categorie</label>
                            <select value={editForm.category} onChange={(e) => setEditValue("category", e.target.value)}>
                                <option value="">--</option>
                                <option value="POSTE_FIXE">Poste fixe</option>
                                <option value="MENSUEL">Mensuel</option>
                                <option value="HORAIRE">Horaire</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="um-form-section">
                    <h3 className="um-form-title">Informations personnelles</h3>
                    <div className="um-form-grid">
                        <div className="um-field">
                            <label>CIN</label>
                            <input value={editForm.cin} onChange={(e) => setEditValue("cin", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>CNSS</label>
                            <input value={editForm.cnss} onChange={(e) => setEditValue("cnss", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Passeport</label>
                            <input value={editForm.passeport} onChange={(e) => setEditValue("passeport", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Carte sejour</label>
                            <input value={editForm.carteSejour} onChange={(e) => setEditValue("carteSejour", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Statut marital</label>
                            <input value={editForm.statutMarital} onChange={(e) => setEditValue("statutMarital", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Nombre d'enfants</label>
                            <input type="number" min="0" value={editForm.nombreEnfants} onChange={(e) => setEditValue("nombreEnfants", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Ville</label>
                            <input value={editForm.city} onChange={(e) => setEditValue("city", e.target.value)} />
                        </div>
                        <div className="um-field um-field-full">
                            <label>Adresse</label>
                            <input value={editForm.adresse} onChange={(e) => setEditValue("adresse", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Code postal</label>
                            <input value={editForm.codePostal} onChange={(e) => setEditValue("codePostal", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Telephone urgence</label>
                            <input value={editForm.phoneUrgence} onChange={(e) => setEditValue("phoneUrgence", e.target.value)} />
                        </div>
                        <div className="um-field um-field-full">
                            <label>Personne urgence</label>
                            <input value={editForm.personneUrgence} onChange={(e) => setEditValue("personneUrgence", e.target.value)} />
                        </div>
                        <div className="um-field um-field-full">
                            <label>Bio</label>
                            <textarea rows={3} value={editForm.bio} onChange={(e) => setEditValue("bio", e.target.value)} />
                        </div>
                    </div>
                </div>

                <div className="um-form-section">
                    <h3 className="um-form-title">Informations bancaires</h3>
                    <div className="um-form-grid">
                        <div className="um-field">
                            <label>Attestation de RIB (PDF)</label>
                            <input type="file" accept="application/pdf,.pdf" onChange={(e) => setEditValue("attestationRibFile", e.target.files?.[0] || null)} />
                            {editForm.attestationRib && !editForm.attestationRibFile && <small>{editForm.attestationRib}</small>}
                        </div>
                        <div className="um-field">
                            <label>RIB</label>
                            <input value={editForm.rib} onChange={(e) => setEditValue("rib", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>IBAN</label>
                            <input value={editForm.iban} onChange={(e) => setEditValue("iban", e.target.value)} />
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal open={bulkOpen} title="Importation massive (Excel)" onClose={closeBulkModal} footer={
                <>
                    <button className="um-btn um-btn-outline" onClick={closeBulkModal}>Annuler</button>
                    <button className="um-btn um-btn-primary" onClick={onImport} disabled={!massImportReview || hasErrors || !rows.length}>Confirmer</button>
                </>
            }>
                <div className="um-form-section">
                    <h3 className="um-form-title">Paramètres</h3>
                    <div className="um-form-grid">
                        <div className="um-field um-field-full">
                            <label>Action</label>
                            <div className="um-segmented">
                                <button className={`um-seg-btn ${bulkAction === "add" ? "active" : ""}`} onClick={() => setBulkAction("add")}>Ajouter</button>
                                <button className={`um-seg-btn ${bulkAction === "edit" ? "active" : ""}`} onClick={() => setBulkAction("edit")}>Mettre à jour</button>
                            </div>
                        </div>
                        <div className="um-field um-field-full">
                            <label>Fichier</label>
                            <label className="um-file-drop">
                                <FileSpreadsheet size={18} style={{ color: '#2a5ca9' }} />
                                <span>{fileName || "Cliquez pour choisir un fichier Excel"}</span>
                                <input type="file" accept=".xlsx,.xls" hidden onChange={(e) => handleFile(e.target.files?.[0])} />
                            </label>
                        </div>
                    </div>
                    <button className="um-btn um-btn-outline w-100 mt-3" onClick={() => dispatch(reviewUsersMassImportRequest(currentCompanyId, bulkAction, rows))} disabled={!rows.length}>
                        Vérifier le fichier
                    </button>
                </div>
                {rows.length > 0 && (
                    <div className="um-form-section">
                        <h3 className="um-form-title">Previsualisation</h3>
                        <div className="um-alert um-alert-success">
                            <CheckCircle size={16} />
                            {rows.length} ligne(s) detectee(s). Apercu des 5 premieres lignes avant import.
                        </div>
                        <div className="um-table-wrapper um-table-wrapper-compact">
                            <table className="um-table">
                                <thead>
                                <tr>
                                    <th>Matricule</th>
                                    <th>Nom</th>
                                    <th>Email</th>
                                    <th>Poste</th>
                                    <th>Entite</th>
                                    <th>Contrat</th>
                                </tr>
                                </thead>
                                <tbody>
                                {rows.slice(0, 5).map((row, index) => (
                                    <tr key={`${row.employeeCode || "row"}-${index}`}>
                                        <td><span className="um-strong">{row.employeeCode || "-"}</span></td>
                                        <td>{`${row.firstName || ""} ${row.lastName || ""}`.trim() || "-"}</td>
                                        <td>{row.email || "-"}</td>
                                        <td>{row.jobCode || "-"}</td>
                                        <td>{row.entityCode || "-"}</td>
                                        <td>{row.typeContrat || "-"}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                {massImportReview && (
                    <div className="um-form-section">
                        <h3 className="um-form-title">Résultat</h3>
                        {!hasErrors ? (
                            <div className="um-alert um-alert-success"><CheckCircle size={16} /> Prêt pour l'import ({rows.length} lignes)</div>
                        ) : (
                            <div className="um-alert um-alert-danger">
                                <XCircle size={16} />
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    {massImportReview.errors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* ── Modal Création individuelle ── */}
            <Modal open={createOpen} title="Ajouter un utilisateur" onClose={closeCreate} footer={
                <>
                    <button className="um-btn um-btn-outline" onClick={closeCreate}>Annuler</button>
                    <button className="um-btn um-btn-primary" onClick={onCreateUser}>
                        <Save size={14} /> Créer
                    </button>
                </>
            }>
                <div className="um-form-section">
                    <h3 className="um-form-title">Identité</h3>
                    <div className="um-form-grid">
                        <div className="um-field">
                            <label>Matricule</label>
                            <input value={createForm.employeeCode} onChange={(e) => setCreateValue("employeeCode", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Prénom <span className="um-required">*</span></label>
                            <input value={createForm.firstName} onChange={(e) => setCreateValue("firstName", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Nom <span className="um-required">*</span></label>
                            <input value={createForm.lastName} onChange={(e) => setCreateValue("lastName", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Email</label>
                            <input type="email" value={createForm.email} onChange={(e) => setCreateValue("email", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Mot de passe</label>
                            <input type="password" value={createForm.password} onChange={(e) => setCreateValue("password", e.target.value)} placeholder="Obligatoire si un e-mail est renseigné" />
                        </div>
                        <div className="um-field">
                            <label>Téléphone</label>
                            <input value={createForm.phone} onChange={(e) => setCreateValue("phone", e.target.value)} />
                        </div>
                    </div>
                </div>

                <div className="um-form-section">
                    <h3 className="um-form-title">Informations RH</h3>
                    <div className="um-form-grid">
                        <div className="um-field">
                            <label>Date d'embauche</label>
                            <input type="date" value={createForm.hireDate} onChange={(e) => setCreateValue("hireDate", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Date de naissance</label>
                            <input type="date" value={createForm.dateOfBirth} onChange={(e) => setCreateValue("dateOfBirth", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Genre</label>
                            <select value={createForm.genre} onChange={(e) => setCreateValue("genre", e.target.value)}>
                                <option value="">-- Non renseigné --</option>
                                <option value="F">Femme</option>
                                <option value="H">Homme</option>
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Poste</label>
                            <select value={createForm.jobId} onChange={(e) => setCreateValue("jobId", e.target.value)}>
                                <option value="">-- Aucun poste --</option>
                                {(jobs || []).map((job) => (
                                    <option key={job.id} value={String(job.id)}>
                                        {[job.code, job.name].filter(Boolean).join(" - ") || `Poste #${job.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Entité</label>
                            <select value={createForm.entityId} onChange={(e) => setCreateValue("entityId", e.target.value)}>
                                <option value="">-- Aucune entité --</option>
                                {(entites || []).map((entite) => (
                                    <option key={entite.id} value={String(entite.id)}>
                                        {[entite.code, entite.name].filter(Boolean).join(" - ") || `Entité #${entite.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Manager</label>
                            <select value={createForm.managerId} onChange={(e) => setCreateValue("managerId", e.target.value)}>
                                <option value="">-- Aucun manager --</option>
                                {(users || []).filter(isUserActive).map((u) => (
                                    <option key={u.id} value={String(u.id)}>
                                        {u.employeeCode ? `[${u.employeeCode}] ` : ""}{userFullName(u)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="um-field">
                            <label>État du compte</label>
                            <select value={createForm.accountStatus} onChange={(e) => setCreateValue("accountStatus", e.target.value)}>
                                {ACCOUNT_STATUS_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Type contrat</label>
                            <select value={createForm.typeContrat} onChange={(e) => setCreateValue("typeContrat", e.target.value)}>
                                <option value="">--</option>
                                <option value="CDI">CDI</option>
                                <option value="CDD">CDD</option>
                            </select>
                        </div>
                        <div className="um-field">
                            <label>Catégorie</label>
                            <select value={createForm.category} onChange={(e) => setCreateValue("category", e.target.value)}>
                                <option value="">--</option>
                                <option value="POSTE_FIXE">Poste fixe</option>
                                <option value="MENSUEL">Mensuel</option>
                                <option value="HORAIRE">Horaire</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="um-form-section">
                    <h3 className="um-form-title">Informations personnelles</h3>
                    <div className="um-form-grid">
                        <div className="um-field">
                            <label>CIN</label>
                            <input value={createForm.cin} onChange={(e) => setCreateValue("cin", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>CNSS</label>
                            <input value={createForm.cnss} onChange={(e) => setCreateValue("cnss", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Passeport</label>
                            <input value={createForm.passeport} onChange={(e) => setCreateValue("passeport", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Carte séjour</label>
                            <input value={createForm.carteSejour} onChange={(e) => setCreateValue("carteSejour", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Statut marital</label>
                            <input value={createForm.statutMarital} onChange={(e) => setCreateValue("statutMarital", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Nombre d'enfants</label>
                            <input type="number" min="0" value={createForm.nombreEnfants} onChange={(e) => setCreateValue("nombreEnfants", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Ville</label>
                            <input value={createForm.city} onChange={(e) => setCreateValue("city", e.target.value)} />
                        </div>
                        <div className="um-field um-field-full">
                            <label>Adresse</label>
                            <input value={createForm.adresse} onChange={(e) => setCreateValue("adresse", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Code postal</label>
                            <input value={createForm.codePostal} onChange={(e) => setCreateValue("codePostal", e.target.value)} />
                        </div>
                        <div className="um-field">
                            <label>Téléphone urgence</label>
                            <input value={createForm.phoneUrgence} onChange={(e) => setCreateValue("phoneUrgence", e.target.value)} />
                        </div>
                        <div className="um-field um-field-full">
                            <label>Personne urgence</label>
                            <input value={createForm.personneUrgence} onChange={(e) => setCreateValue("personneUrgence", e.target.value)} />
                        </div>
                        <div className="um-field um-field-full">
                            <label>Bio</label>
                            <textarea rows={3} value={createForm.bio} onChange={(e) => setCreateValue("bio", e.target.value)} />
                        </div>
                    </div>
                </div>

                <div className="um-form-section">
                    <h3 className="um-form-title">Informations bancaires</h3>
                    <div className="um-form-grid">
                        <div className="um-field">
                            <label>RIB</label>
                            <input value={createForm.rib} onChange={(e) => setCreateValue("rib", e.target.value)} />
                        </div>
                    </div>
                </div>
            </Modal>

                        {/* ── Modal Import RIB / Banque ── */}
            <Modal open={ribModalOpen} title="Importation RIB & Banque (Excel)" onClose={closeRibModal} footer={
                <>
                    <button className="um-btn um-btn-outline" onClick={closeRibModal}>Annuler</button>
                    <button className="um-btn um-btn-primary" onClick={onImportRib} disabled={!ribRows.length}>
                        Confirmer l'affectation
                    </button>
                </>
            }>
                <div className="um-form-section">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                        <h3 className="um-form-title" style={{ margin: 0 }}>Sélectionner le fichier Excel</h3>
                        <button className="um-btn um-btn-outline" onClick={downloadRibTemplate} type="button" style={{ fontSize: "12px", padding: "4px 8px" }}>
                            <Download size={13} /> Template RIB/Banque
                        </button>
                    </div>
                    <div className="um-form-grid">
                        <div className="um-field um-field-full">
                            <label className="um-file-drop">
                                <FileSpreadsheet size={18} style={{ color: "#2a5ca9" }} />
                                <span>{ribFileName || "Cliquez pour choisir un fichier Excel (Matricule, RIB, Banque...)"}</span>
                                <input type="file" accept=".xlsx,.xls" hidden onChange={(e) => handleRibFile(e.target.files?.[0])} />
                            </label>
                        </div>
                    </div>
                </div>
                {ribRows.length > 0 && (
                    <div className="um-form-section">
                        <h3 className="um-form-title">Aperçu ({ribRows.length} lignes)</h3>
                        <div className="um-table-wrapper um-table-wrapper-compact">
                            <table className="um-table">
                                <thead>
                                    <tr>
                                        <th>Matricule</th>
                                        <th>RIB</th>
                                        <th>Banque</th>
                                        <th>IBAN</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ribRows.slice(0, 10).map((row, index) => (
                                        <tr key={`${row.employeeCode}-${index}`}>
                                            <td><span className="um-strong">{row.employeeCode || "-"}</span></td>
                                            <td>{row.rib || "-"}</td>
                                            <td>{row.banque || "-"}</td>
                                            <td>{row.iban || "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </Modal>

            <ScrollToTop />
        </div>
    );
}
